import { useMemo, useState } from "react";

/**
 * Monthly net revenue.
 *
 * Built to the spec in site/revenue-chart.prompt.md. The one requirement that
 * is a correctness issue rather than a styling one: NET REVENUE GOES NEGATIVE
 * in any month where credit memos exceed invoices, and on live data the two
 * most recent months of a real customer are −38,928.50 and −9,808.83. A chart
 * anchored at zero either clips those or drops them — hiding the two months a
 * salesperson looks at first. So the scale diverges around a zero rule.
 *
 * Colours encode polarity only and were validated for colour-vision
 * deficiency against their own surfaces. They are not the only signal: a
 * negative column also hangs below the zero rule and says so in its tooltip.
 */

interface Point {
  month: string;
  netRevenue: number;
  grossInvoiced: number;
}

const RANGES = [
  { label: "12M", months: 12 },
  { label: "24M", months: 24 },
  { label: "All", months: Infinity },
] as const;

/** Nice-number axis steps: 1 / 2 / 2.5 / 5 x 10^n. */
function niceStep(rough: number): number {
  if (rough <= 0) return 1;
  const exp = Math.floor(Math.log10(rough));
  const base = Math.pow(10, exp);
  const frac = rough / base;
  const mult = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return mult * base;
}

function compact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs % 1_000 === 0 ? 0 : 1)}K`;
  return String(Math.round(value));
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(m) - 1] ?? m} ${y?.slice(2) ?? ""}`;
}

function fullMonthLabel(month: string): string {
  const [y, m] = month.split("-");
  const names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${names[Number(m) - 1] ?? m} ${y}`;
}

export function RevenueChart({
  points,
  currency,
  truncated,
}: {
  points: Point[];
  currency: string | null;
  truncated: boolean;
}) {
  const [rangeIdx, setRangeIdx] = useState(0);
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);

  const visible = useMemo(() => {
    const months = RANGES[rangeIdx]!.months;
    return months === Infinity ? points : points.slice(-months);
  }, [points, rangeIdx]);

  const scale = useMemo(() => {
    if (!visible.length) return null;
    const values = visible.map((p) => p.netRevenue);
    const rawMax = Math.max(0, ...values);
    const rawMin = Math.min(0, ...values);
    const span = rawMax - rawMin || 1;
    // 4-6 ticks across the span, then extend OUTWARD to whole steps so the
    // tallest column never touches the top gridline.
    const step = niceStep(span / 4);
    const max = Math.ceil(rawMax / step) * step;
    const min = Math.floor(rawMin / step) * step;
    const ticks: number[] = [];
    for (let v = min; v <= max + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
    if (!ticks.includes(0)) ticks.push(0);
    ticks.sort((a, b) => a - b);
    return { min, max: max === min ? min + step : max, ticks };
  }, [visible]);

  if (!points.length) {
    return <p className="muted">No invoiced revenue in the period.</p>;
  }

  const H = 240;
  const toY = (v: number) => {
    if (!scale) return H;
    return H - ((v - scale.min) / (scale.max - scale.min)) * H;
  };

  // Label only the highest and the most recent — a value on every column is
  // what collides with the axis.
  const peakIdx = visible.reduce((best, p, i) => (p.netRevenue > (visible[best]?.netRevenue ?? -Infinity) ? i : best), 0);
  const lastIdx = visible.length - 1;
  const labelled = new Set([peakIdx, lastIdx]);

  const controls = (
    <div className="chart-controls">
      <div className="seg">
        {RANGES.map((r, i) => (
          <button key={r.label} className={i === rangeIdx ? "on" : ""} onClick={() => setRangeIdx(i)}>
            {r.label}
          </button>
        ))}
      </div>
      <button className="seg-single" onClick={() => setAsTable((v) => !v)}>
        {asTable ? "Chart" : "Table"}
      </button>
    </div>
  );

  if (asTable) {
    return (
      <div>
        {controls}
        <table className="data">
          <thead>
            <tr><th>Month</th><th className="num">Net revenue</th><th className="num">Gross invoiced</th></tr>
          </thead>
          <tbody>
            {[...visible].reverse().map((p) => (
              <tr key={p.month}>
                <td>{fullMonthLabel(p.month)}</td>
                <td className="num">{p.netRevenue.toFixed(2)}</td>
                <td className="num">{p.grossInvoiced.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const colW = 100 / visible.length;
  const hovered = hover !== null ? visible[hover] : null;

  return (
    <div>
      {controls}
      <div className="chart">
        <div className="chart-axis">
          {scale?.ticks.map((t, i) => (
            <div key={t} className="tick" style={{ top: `${(toY(t) / H) * 100}%` }}>
              <span>{i === (scale.ticks.length - 1) && currency ? `${currency} ` : ""}{compact(t)}</span>
            </div>
          ))}
        </div>
        <div className="chart-plot" style={{ height: H }}>
          {scale?.ticks.map((t) => (
            <div key={t} className={t === 0 ? "grid zero" : "grid"} style={{ top: toY(t) }} />
          ))}
          {visible.map((p, i) => {
            const y0 = toY(0);
            const y1 = toY(p.netRevenue);
            const negative = p.netRevenue < 0;
            return (
              <div
                key={p.month}
                className="col-hit"
                style={{ left: `${i * colW}%`, width: `${colW}%` }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                tabIndex={0}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                aria-label={`${fullMonthLabel(p.month)}: ${p.netRevenue.toFixed(2)}`}
              >
                <div
                  className={negative ? "col neg" : "col"}
                  style={{ top: Math.min(y0, y1), height: Math.max(1, Math.abs(y1 - y0)) }}
                />
                {labelled.has(i) && (
                  <span className="col-label" style={{ top: negative ? Math.max(y0, y1) + 2 : Math.min(y0, y1) - 18 }}>
                    {compact(p.netRevenue)}
                  </span>
                )}
              </div>
            );
          })}
          {hovered && (
            <div className="tip" style={{ left: `${(visible.indexOf(hovered) + 0.5) * colW}%` }}>
              <strong>{fullMonthLabel(hovered.month)}</strong>
              <span>Net revenue {hovered.netRevenue.toFixed(2)}</span>
              {hovered.grossInvoiced !== hovered.netRevenue && (
                <span className="muted">Gross invoiced {hovered.grossInvoiced.toFixed(2)}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="chart-x">
        {visible.map((p, i) => {
          const show = p.month.endsWith("-01") || i === lastIdx;
          return (
            <span key={p.month} style={{ width: `${colW}%` }}>{show ? monthLabel(p.month) : ""}</span>
          );
        })}
      </div>
      {truncated && <p className="muted small">Series clipped to a rolling window.</p>}
    </div>
  );
}
